import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Group,
  Text,
  Title,
  Box,
  Stack,
  useComputedColorScheme,
} from '@mantine/core';
import { IconArrowRight, IconArrowLeft, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../store/userStore';
import { useDevice } from '../../hooks/useDevice';
import { hasCompanyGrant, useMyPermissions } from '../../hooks/usePermission';
import { useCompleteWizard } from '../../hooks/useOnboardingApi';
import { WIZARD_GIFS } from './constants';
import { useAnalytics, AnalyticsEvents } from '../../analytics';
import { useCompanyModules } from '../../hooks/useCompanyModules';

export const OnboardingWizard: React.FC = () => {
  const { t } = useTranslation();
  const user = useUserStore((s) => s.user);
  const { data: perms, isPending: isPermsPending } = useMyPermissions();
  const deviceType = useDevice();
  const completeWizard = useCompleteWizard();
  const colorScheme = useComputedColorScheme('light');
  const { track } = useAnalytics();
  const { hasModule, isLoaded: isModulesLoaded } = useCompanyModules();
  const [active, setActive] = useState(0);
  const [isDismissed, setDismissed] = useState(false);
  const [imgStatus, setImgStatus] = useState<Record<number, 'loading' | 'ok' | 'error'>>({});

  // Track onboarding started
  useEffect(() => {
    if (user && !user.onboardingState?.wizardCompleted) {
      track(AnalyticsEvents.ONBOARDING_STARTED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // «Показать тур снова» сбрасывает wizardCompleted — тогда открываем тур с начала,
  // даже если его уже закрыли в этой сессии.
  const isWizardCompleted = user?.onboardingState?.wizardCompleted;
  useEffect(() => {
    if (isWizardCompleted === false) {
      setDismissed(false);
      setActive(0);
    }
  }, [isWizardCompleted]);

  if (user === undefined) return null;
  if (isDismissed) return null;
  // Wait for full user data — login response has no onboardingState
  if (user.onboardingState === undefined) return null;
  if (user.onboardingState?.wizardCompleted === true) return null;
  // Число шагов зависит от прав — не показываем тур, пока они не пришли.
  if (isPermsPending || !isModulesLoaded) return null;

  const isDark = colorScheme === 'dark';
  // Шаг «Управление» (карты, люди, команды) — тем, кто настраивает компанию.
  const canSetUpCompany = hasCompanyGrant(perms, 'company.settings');
  // Шаги 2–4 — про карту компетенций: без этого раздела компании их нет, а тексты остальных —
  // без компетенций и грейдов.
  const hasMap = hasModule('competencyMap');
  const textKey = (step: string) => `onboarding.wizard.${step}.${hasMap ? 'text' : 'textNoMap'}`;

  const mapSteps = [
    {
      title: t('onboarding.wizard.step2.title'),
      text: t('onboarding.wizard.step2.text'),
      gif: WIZARD_GIFS.step2,
      icon: '🗺️',
    },
    {
      title: t('onboarding.wizard.step3.title'),
      text: t('onboarding.wizard.step3.text'),
      gif: WIZARD_GIFS.step3,
      icon: '👤',
    },
    {
      title: t('onboarding.wizard.step4.title'),
      text: t('onboarding.wizard.step4.text'),
      gif: WIZARD_GIFS.step4,
      icon: '🚀',
    },
  ];
  const steps = [
    {
      title: t('onboarding.wizard.step1.title'),
      text: t(textKey('step1')),
      // Записи экрана показывают полный продукт (карта, грейды, курсы): без карты — без них.
      gif: hasMap ? WIZARD_GIFS.step1 : undefined,
      icon: '👋',
    },
    ...(hasMap ? mapSteps : []),
    ...(canSetUpCompany
      ? [
          {
            title: t('onboarding.wizard.step5.title'),
            text: t(textKey('step5')),
            gif: hasMap ? WIZARD_GIFS.step5 : undefined,
            icon: '⚙️',
          },
        ]
      : []),
  ];
  const totalSteps = steps.length;

  const isLastStep = active === totalSteps - 1;
  const currentStep = steps[active];
  const mediaSrc = currentStep.gif?.[isDark ? 'dark' : 'light'];

  const handleComplete = () => {
    if (isLastStep) {
      track(AnalyticsEvents.ONBOARDING_COMPLETED, { totalSteps });
    } else {
      track(AnalyticsEvents.ONBOARDING_SKIPPED, { atStep: active + 1, totalSteps });
    }
    setDismissed(true);
    completeWizard.mutate();
  };

  const handleNext = () => {
    if (isLastStep) {
      track(AnalyticsEvents.ONBOARDING_STEP_COMPLETED, { step: active + 1, totalSteps });
      handleComplete();
    } else {
      track(AnalyticsEvents.ONBOARDING_STEP_COMPLETED, { step: active + 1, totalSteps });
      setActive((prev) => prev + 1);
    }
  };

  const handleBack = () => setActive((prev) => Math.max(0, prev - 1));

  return (
    <Modal
      opened
      onClose={handleComplete}
      size={560}
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
      fullScreen={deviceType === 'mobile'}
      centered
      radius="lg"
      padding={0}
      styles={{
        body: { padding: 0 },
        content: {
          overflow: 'hidden',
        },
      }}
    >
      {/* Header with progress dots + skip */}
      <Group justify="space-between" px="xl" pt="lg" pb="xs">
        <Group gap={6}>
          {steps.map((_, i) => (
            <Box
              key={i}
              style={{
                width: i === active ? 24 : 8,
                height: 8,
                borderRadius: 4,
                backgroundColor:
                  i === active
                    ? 'var(--mantine-color-teal-6)'
                    : i < active
                      ? 'var(--mantine-color-teal-3)'
                      : isDark
                        ? 'var(--mantine-color-dark-4)'
                        : 'var(--mantine-color-gray-3)',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </Group>
        <Button
          variant="subtle"
          color="gray"
          size="compact-sm"
          onClick={handleComplete}
          rightSection={<IconX size={14} />}
        >
          {t('onboarding.wizard.skipButton')}
        </Button>
      </Group>

      {/* Content */}
      <Stack align="center" px="xl" pb="md" pt="xs" gap="md">
        {/* Icon */}
        <Text size="40px" style={{ lineHeight: 1 }}>
          {currentStep.icon}
        </Text>

        {/* Title */}
        <Title order={3} ta="center" fw={700}>
          {currentStep.title}
        </Title>

        {/* Description */}
        <Text ta="center" c="dimmed" size="sm" maw={420} lh={1.6}>
          {currentStep.text}
        </Text>

        {/* Theme-matched screen recording with graceful fallback to a placeholder */}
        {!currentStep.gif ? null : mediaSrc && imgStatus[active] !== 'error' ? (
          <Box
            style={{
              width: '100%',
              maxWidth: 480,
              borderRadius: 'var(--mantine-radius-md)',
              overflow: 'hidden',
              border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
            }}
          >
            <video
              key={mediaSrc}
              src={mediaSrc}
              autoPlay
              loop
              muted
              playsInline
              onError={() => setImgStatus((prev) => ({ ...prev, [active]: 'error' }))}
              style={{ width: '100%', display: 'block' }}
            />
          </Box>
        ) : (
          <Box
            style={{
              width: '100%',
              maxWidth: 480,
              height: 200,
              borderRadius: 'var(--mantine-radius-md)',
              border: `2px dashed ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text c="dimmed" size="sm">
              {currentStep.icon} GIF будет добавлен позже
            </Text>
          </Box>
        )}
      </Stack>

      {/* Footer with navigation */}
      <Group
        justify="space-between"
        px="xl"
        py="md"
        style={{
          borderTop: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-2)'}`,
        }}
      >
        <Button
          variant="subtle"
          color="gray"
          onClick={handleBack}
          disabled={active === 0}
          leftSection={<IconArrowLeft size={16} />}
        >
          {t('onboarding.wizard.backButton')}
        </Button>

        {/* Step counter */}
        <Text size="sm" c="dimmed">
          {active + 1} / {totalSteps}
        </Text>

        <Button
          onClick={handleNext}
          rightSection={!isLastStep ? <IconArrowRight size={16} /> : undefined}
        >
          {isLastStep ? t('onboarding.wizard.doneButton') : t('onboarding.wizard.nextButton')}
        </Button>
      </Group>
    </Modal>
  );
};
